import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useKeycloak } from '@/context/KeycloakContext';
import { getLanguage } from '@/i18n';
import { resolveReturnTo, SSO_CALLBACK_PATH, withReturnTo } from '@/lib/utils/login-redirect';

export function Login() {
	const { t } = useTranslation();
	const { keycloak, authenticated } = useKeycloak();
	const navigate = useNavigate();
	const location = useLocation();
	// The page the user asked for before being sent here (see login-redirect.ts).
	const returnTo = resolveReturnTo(location.search);
	const ssoRedirectUri = `${window.location.origin}${withReturnTo(SSO_CALLBACK_PATH, returnTo)}`;

	console.log('keycloak in login page', {
		token: keycloak?.token,
		authenticated,
		identity: keycloak?.idTokenParsed,
		idToken: keycloak?.idToken,
	});

	useEffect(() => {
		if (authenticated && keycloak?.token) {
			navigate(returnTo, { replace: true });
		}
	}, [authenticated, keycloak, navigate, returnTo]);

	// Keycloak reads `locale` as ui_locales, so its login page opens in the app's language.
	// redirect to Keycloak for login if not authenticated - this is optional, as we also have a form-based login
	useEffect(() => {
		if (!keycloak?.didInitialize) {
			return;
		}
		if (!authenticated) {
			keycloak.login({ redirectUri: ssoRedirectUri, locale: getLanguage() });
		}
	}, [authenticated, keycloak?.didInitialize, keycloak?.login, ssoRedirectUri]);

	// Keycloak SSO login
	const onKeycloakLogin = async () => {
		if (!keycloak) {
			toast.error(t('auth.toast.notInitialized'));
			return;
		}
		try {
			await keycloak.login({ redirectUri: ssoRedirectUri, locale: getLanguage() });
			console.log('Keycloak login successful');
			toast.success(t('auth.toast.loginSuccess'));
		} catch (error) {
			console.error('Keycloak login failed:', error);
			toast.error(t('auth.toast.loginFailed'));
		}
	};
	return (
		<div className="min-h-screen flex items-center justify-center">
			<div className="text-center">
				<h1 className="text-4xl font-bold mb-4">{t('auth.login.redirecting')}</h1>
				<p className="text-lg text-muted-foreground">{t('auth.login.redirectFallback')}</p>
				<Button
					onClick={onKeycloakLogin}
					className="mt-6 bg-blue-600 hover:bg-blue-700 text-white"
					disabled={!keycloak?.didInitialize}
				>
					{t('auth.login.loginButton')}
				</Button>
			</div>
		</div>
	);
}
